"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Label } from "@repo/ui";
import { Input } from "@repo/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@repo/ui";
import posthog from "posthog-js";
import { Card, CardHeader, CardDescription, CardTitle, CardContent } from "@repo/ui";
import { Loader2, AlertTriangle } from "lucide-react";
import { useAnalytics } from "@/hooks/use-analytics";
import { usePyloStatus } from "@/hooks/use-pylo-status";
import MicrosoftIcon from "@/components/microsoft-icon";
import GoogleIcon from "@/components/google-icon";
import { useQueryState } from "nuqs";
import Link from "next/link";
import { login } from "@/services/auth.service";
import { originForSubdomain } from "@/lib/admin-hosts";
import { PyloUserInvitePublicData } from "@/services/pylo-user.service";

interface LoginFormProps {
  inviteCode?: string;
  inviteData?: PyloUserInvitePublicData;
  hostAppId?: string;
  hostCustomerName?: string;
}

export const LoginForm = ({
  inviteCode,
  inviteData,
  hostAppId,
  hostCustomerName,
}: LoginFormProps = {}) => {
  const router = useRouter();

  const { isUnreachable: backendUnreachable } = usePyloStatus();
  const [email, setEmail] = useState(
    inviteData?.email ?? process.env.NEXT_PUBLIC_DEFAULT_USER_EMAIL ?? "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [password, setPassword] = useState(process.env.NEXT_PUBLIC_DEFAULT_USER_PW ?? "");

  const [error] = useQueryState("error");

  const searchParams = useSearchParams();
  const redirectPath = searchParams.get("redirect");

  const queryClient = useQueryClient();

  const { capture } = useAnalytics();

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const result = await login(email, password, inviteCode, hostAppId);

      if (!result.success) {
        throw new Error(result.error || "Login failed");
      }

      return result;
    },
    onMutate: () => {
      setIsLoading(true);
      setTimeout(() => {
        setShowCancelButton(true);
      }, 3000);
    },
    onSuccess: async () => {
      try {
        capture("login", {
          email,
        });

        // Clear any stale queries before redirect
        queryClient.clear();
        posthog.reset();

        router.push(redirectPath ?? "/");
      } catch {
        capture("login_failed", {
          email,
        });
      } finally {
        setShowCancelButton(false);
        setIsLoading(false);
      }
    },
    onError: (error) => {
      console.error(error);
      setIsLoading(false);
    },
  });

  const noAccess = loginMutation.error?.message === "no_access";
  const renderNoAccess = () => (
    <p className="text-sm text-red-500">
      You don&apos;t have access to {hostCustomerName ?? "this organization"}.{" "}
      <a
        href={`${originForSubdomain(null, window.location.protocol)}/p/auth/login`}
        className="underline"
      >
        Sign in on the main site
      </a>
    </p>
  );

  const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    loginMutation.mutate({ email, password });
  };

  // Everything the social flows need (client id, state, PKCE) is assembled by the start
  // route, so these are plain links and no OAuth parameters are exposed to the browser.
  const signInHref = (provider: "google" | "microsoft") => {
    const params = new URLSearchParams();
    if (inviteCode) params.set("invite", inviteCode);
    if (redirectPath) params.set("redirect", redirectPath);
    if (hostAppId && !inviteCode) params.set("app_id", hostAppId);
    const query = params.toString();
    return `/api/auth/${provider}/start${query ? `?${query}` : ""}`;
  };

  if (isLoading) {
    return (
      <div className="grid h-full w-full place-content-center">
        <div className="flex flex-col items-center gap-4">
          <div>We are getting your environment ready...</div>
          <Loader2 className="h-10 w-10 animate-spin" />
          {showCancelButton && (
            <Button variant={"ghost"} onClick={() => setIsLoading(false)}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (inviteData) {
    return (
      <div className="grid h-full w-full place-content-center">
        {backendUnreachable && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            We&apos;re experiencing some technical difficulties and are working on a fix.
          </div>
        )}
        <Card className="mx-auto max-w-sm">
          <CardHeader>
            <CardTitle className="text-2xl">Join {inviteData.customer_name}</CardTitle>
            <CardDescription>
              You&apos;ve been invited as{" "}
              <span className="font-medium text-foreground">{inviteData.email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="grid gap-4" onSubmit={handleLogin}>
              <div className="grid gap-2">
                <Label htmlFor="password">Choose a password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a secure password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                />
              </div>
              <Button
                variant={"secondary"}
                type="submit"
                className="w-full"
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending ? "Setting up your account..." : "Get Started"}
              </Button>
              {noAccess && renderNoAccess()}
              {loginMutation.isError && !noAccess && (
                <p className="text-sm text-red-500">Something went wrong. Please try again.</p>
              )}
            </form>
            <div className="pb-6">
              <div className="my-4 flex items-center px-6">
                <div className="flex-grow border-t border-gray-200" />
                <span className="mx-4 select-none text-xs font-medium text-muted-foreground">
                  OR CONTINUE WITH
                </span>
                <div className="flex-grow border-t border-gray-200" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button asChild variant="outline">
                  <a href={signInHref("microsoft")} aria-label="Log in with Microsoft">
                    <MicrosoftIcon className="h-5 w-5" />
                  </a>
                </Button>
                <Button asChild variant="outline">
                  <a href={signInHref("google")} aria-label="Log in with Google">
                    <GoogleIcon className="h-5 w-5" />
                  </a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid h-full w-full place-content-center">
      {backendUnreachable && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          We&apos;re experiencing some technical difficulties and are working on a fix.
        </div>
      )}
      <Card className="mx-auto max-w-sm">
        <CardHeader>
          <CardTitle className="text-2xl">Log In</CardTitle>
          <CardDescription>Enter your email address and password to log in</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4" onSubmit={handleLogin}>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                required
                value={email}
                tabIndex={1}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center">
                <Label htmlFor="password">Password</Label>
                <Link
                  href={`/p/auth/password-reset${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                  className="ml-auto text-sm text-muted-foreground underline"
                  tabIndex={3}
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                tabIndex={2}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button
              variant={"secondary"}
              type="submit"
              className="w-full"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? "Logging in..." : "Log In"}
            </Button>
            {noAccess && renderNoAccess()}
            {loginMutation.isError && !noAccess && (
              <p className="text-red-500">Login failed. Please try again.</p>
            )}
          </form>
          <div className="pb-6">
            <div className="my-4 flex items-center px-6">
              <div className="flex-grow border-t border-gray-200" />
              <span className="mx-4 select-none text-xs font-medium text-muted-foreground">
                OR CONTINUE WITH
              </span>
              <div className="flex-grow border-t border-gray-200" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button asChild variant="outline">
                <a href={signInHref("microsoft")} aria-label="Log in with Microsoft">
                  <MicrosoftIcon className="h-5 w-5" />
                </a>
              </Button>
              <Button asChild variant="outline">
                <a href={signInHref("google")} aria-label="Log in with Google">
                  <GoogleIcon className="h-5 w-5" />
                </a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <p className="mx-auto pt-4 text-sm text-muted-foreground">
        Don&apos;t have an account?{" "}
        <a
          href="https://pyloapp.com/#get-started"
          className="underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Sign up for our closed beta
        </a>
      </p>
      {error && <div className="mx-auto pt-4 text-red-500">{error}</div>}
    </div>
  );
};
